import React, { useMemo, useState } from "react";
import {
  Stack,
  Text,
  TextInput,
  Select,
  NumberInput,
  Checkbox,
  Button,
  Group,
  Modal,
  Box,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons";
import ArrayTable from "../arrayTable";
import ServiceSelectOptionsEditor from "./ServiceSelectOptionsEditor";
import { ORDER_SERVICE_FIELD_TYPES } from "../../data/orderServiceFieldTypes";

const rowInitValue = () => ({
  key: "",
  label: "",
  cname: "",
  fieldType: "TEXT",
  selectOptions: [],
  required: false,
  sortOrder: 0,
});

const FIELD_TYPE_HELP = {
  SELECT: "Define custom options with label and stored value.",
  SELECT_ORDER_STATUS: "Uses the system order status list.",
  REF_USER: "Searchable user list at service creation.",
  REF_PROVIDER: "Searchable insurer / provider list at service creation.",
  REF_PRODUCT_TYPE: "Searchable product type list at service creation.",
};

const ServiceTypeFieldEditor = ({ form, name = "fields", disabled }) => {
  const [optionsModalIndex, setOptionsModalIndex] = useState(null);
  const fields = form?.values?.[name] || [];

  const tableFields = useMemo(
    () => [
      {
        header: "Label",
        headerWidth: "22%",
        fieldName: "label",
        component: TextInput,
        props: { size: "xs", placeholder: "English label" },
      },
      {
        header: "Label (Chinese)",
        headerWidth: "16%",
        fieldName: "cname",
        component: TextInput,
        props: { size: "xs", placeholder: "中文標籤" },
      },
      {
        header: "Type",
        headerWidth: "22%",
        fieldName: "fieldType",
        component: Select,
        props: {
          size: "xs",
          data: ORDER_SERVICE_FIELD_TYPES,
          searchable: true,
        },
      },
      {
        header: "Required",
        headerWidth: "8%",
        fieldName: "required",
        type: "checkbox",
        component: Checkbox,
        props: { size: "xs" },
      },
      {
        header: "Sort",
        headerWidth: "8%",
        fieldName: "sortOrder",
        component: NumberInput,
        props: { size: "xs", min: 0 },
      },
    ],
    []
  );

  const updateFields = (rows) => {
    form.setFieldValue(name, rows);
  };

  return (
    <Stack spacing="md">
      <Box
        sx={(theme) => ({
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[3]
          }`,
          borderRadius: theme.radius.sm,
          padding: theme.spacing.md,
          overflowX: "auto",
        })}
      >
        <ArrayTable
          form={form}
          name={name}
          fields={tableFields}
          rowInitValue={rowInitValue()}
          readonly={disabled}
          addButtonLabel="Add field"
          addButtonProps={{
            variant: "light",
            leftIcon: <IconPlus size={14} />,
          }}
        />
      </Box>

      {fields.length === 0 ? (
        <Text size="xs" color="dimmed">
          No service fields yet. Click &quot;Add field&quot; to define inputs
          collected when creating a service.
        </Text>
      ) : null}

      {fields.some((row) => row.fieldType === "SELECT") ? (
        <Group spacing="xs">
          {fields.map((row, index) =>
            row.fieldType === "SELECT" ? (
              <Button
                key={`${row.key || index}-options`}
                size="xs"
                variant="subtle"
                w="fit-content"
                onClick={() => setOptionsModalIndex(index)}
              >
                Options: {row.label || `Row ${index + 1}`}
              </Button>
            ) : null
          )}
        </Group>
      ) : null}

      {fields.some((row) => FIELD_TYPE_HELP[row.fieldType]) ? (
        <Stack spacing={4}>
          {fields.map((row, index) => {
            const help = FIELD_TYPE_HELP[row.fieldType];
            if (!help) return null;
            return (
              <Text key={`help-${index}`} size="xs" color="dimmed">
                {row.label || `Row ${index + 1}`}: {help}
              </Text>
            );
          })}
        </Stack>
      ) : null}

      <Modal
        opened={optionsModalIndex != null}
        onClose={() => setOptionsModalIndex(null)}
        title="Custom select options"
        size="lg"
      >
        {optionsModalIndex != null ? (
          <ServiceSelectOptionsEditor
            value={fields[optionsModalIndex]?.selectOptions || []}
            disabled={disabled}
            onChange={(next) => {
              const rows = [...fields];
              rows[optionsModalIndex] = {
                ...rows[optionsModalIndex],
                selectOptions: next,
              };
              updateFields(rows);
            }}
          />
        ) : null}
        <Group position="right" mt="md">
          <Button w="fit-content" onClick={() => setOptionsModalIndex(null)}>
            Done
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default ServiceTypeFieldEditor;
